import { FC, useState, lazy, Suspense } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Download, Pencil, User, X, ExternalLink, Mail } from 'lucide-react'
import FormField from '@/components/settings/FormField'
import { useUserProfile } from '@/hooks/api/useUsers'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { ROUTES } from '@/constants/routes'
import { useNavigate } from '@tanstack/react-router'
import { ArchivePage } from '@/routes/settings/archive'
import { FeedbackPage } from '@/routes/settings/feedback'
import { CURRENCIES } from '@/constants/currencies'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { Modal, ModalHeader, ModalContent } from '@/components/shared/Modal'
import platformSubscriptionsApi from '@wtf/shared/api/platformSubscriptions'
import { usePricing } from '@/hooks/api/usePricing'
import { useSaveButtonColor } from '@/stores/saveButtonColor'
import { SAVE_BUTTON_COLORS } from '@/constants/saveButtonColors'
import { SaveButton } from '@/components/shared/atoms/SaveButton'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents } from '@/utils/analytics'
import { PendingDeletionBanner } from '@/components/settings/PendingDeletionBanner'
import { useUpdateCurrency } from '@/hooks/useUpdateCurrency'
import { useUserPreferences } from '@/stores/userPreferences'
import { useBlockedUsers, useUnblockUser } from '@/hooks/api/useBlocks'
import { useToast } from '@/components/shared/toast/Toast'
import { Link } from '@tanstack/react-router'
import { captureException } from '@/config/sentry'

const CancelSubscriptionBottomSheet = lazy(
    () =>
        import(
            '@/components/subscriptions/bottom-sheets/CancelSubscriptionBottomSheet'
        )
)

const DowngradeSubscriptionBottomSheet = lazy(
    () =>
        import(
            '@/components/subscriptions/bottom-sheets/DowngradeSubscriptionBottomSheet'
        )
)

const DeleteAccountBottomSheet = lazy(
    () => import('@/components/settings/bottom-sheets/DeleteAccount')
)

type AccountPopupProps = {
    onClose: () => void
    initialTab?: string
}

const menuItems = [
    'Account',
    'Currency',
    'Archive',
    'Feedback',
    'Subscription',
    'Save Button Colour',
    'Blocked Users',
    'Contact & Support',
]

const AccountPopup: FC<AccountPopupProps> = ({
    onClose,
    initialTab = 'Account',
}) => {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState(initialTab)
    const { user } = useCurrentUser()
    const { data: profile } = useUserProfile(user?.uid)
    const queryClient = useQueryClient()
    const { preferredCurrency } = useUserPreferences()
    const { updateCurrency } = useUpdateCurrency()
    const [isComingSoonModalOpen, setIsComingSoonModalOpen] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [showDowngradeModal, setShowDowngradeModal] = useState(false)
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false)
    const [targetDowngradePlan, setTargetDowngradePlan] = useState<
        'FREE' | 'PRO' | 'MAX'
    >('FREE')
    const { color: saveButtonColor, setColor: setSaveButtonColor } =
        useSaveButtonColor()
    const posthog = usePostHog()
    const { addToast } = useToast()

    // Blocked users
    const { data: blockedUsersData, isLoading: isLoadingBlockedUsers } = useBlockedUsers()
    const { mutateAsync: unblockUser, isPending: isUnblocking } = useUnblockUser()
    const blockedUsers = blockedUsersData?.blockedUsers ?? []

    const handleUnblock = async (userId: string, username: string) => {
        try {
            await unblockUser(userId)
            addToast(`@${username} has been unblocked`, 'success')
        } catch (error) {
            captureException(error, { action: 'unblock_user', userId, username })
            addToast('Failed to unblock user. Please try again.', 'error')
        }
    }

    const { data: platformSubscription, isLoading: isLoadingSubscription } =
        useQuery({
            queryKey: ['platform-subscription'],
            queryFn: () => platformSubscriptionsApi.getCurrentSubscription(),
            staleTime: 0, // Always fetch fresh data
            refetchOnMount: 'always', // Force refetch when component mounts
        })

    // Fetch billing history
    const { data: billingHistory, isLoading: isLoadingBillingHistory } =
        useQuery({
            queryKey: ['billing-history'],
            queryFn: () => platformSubscriptionsApi.getBillingHistory(),
            staleTime: 5 * 60 * 1000, // 5 minutes
            enabled:
                !!platformSubscription && platformSubscription.plan !== 'FREE',
        })

    // Fetch pricing from Stripe
    const { data: pricing } = usePricing()

    const handleCurrencyChange = async (currency: string) => {
        await updateCurrency(currency, {
            showSuccessToast: true,
        })
    }

    const handleUpgrade = () => {
        const currentPlan = platformSubscription?.plan || 'FREE'
        const nextPlan = currentPlan === 'FREE' ? 'pro' : 'max'
        navigate({
            to: '/pricing',
            search: {
                preselect: nextPlan,
                returnToPath: undefined,
                reason: undefined,
            },
        })
    }

    const handleDowngrade = () => {
        const currentPlan = platformSubscription?.plan || 'FREE'
        if (currentPlan === 'MAX') {
            setTargetDowngradePlan('PRO')
        } else if (currentPlan === 'PRO') {
            setTargetDowngradePlan('FREE')
        }
        setShowDowngradeModal(true)
    }

    const handleCancel = () => {
        setShowCancelModal(true)
    }

    const handleReactivate = async () => {
        try {
            await platformSubscriptionsApi.reactivateSubscription()
            // Refetch subscription to show updated state
            queryClient.invalidateQueries({
                queryKey: ['platform-subscription'],
            })
        } catch (error) {
            console.error('Failed to reactivate subscription:', error)
        }
    }

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A'
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        })
    }

    const getStatusBadge = (status: string) => {
        const statusMap: Record<
            string,
            { bg: string; text: string; label: string }
        > = {
            ACTIVE: {
                bg: 'bg-green-100',
                text: 'text-green-700',
                label: 'Active',
            },
            TRIALING: {
                bg: 'bg-blue-100',
                text: 'text-blue-700',
                label: 'Trialing',
            },
            PAST_DUE: {
                bg: 'bg-yellow-100',
                text: 'text-yellow-700',
                label: 'Past Due',
            },
            CANCELED: {
                bg: 'bg-red-100',
                text: 'text-red-700',
                label: 'Canceled',
            },
            INCOMPLETE: {
                bg: 'bg-gray-100',
                text: 'text-gray-700',
                label: 'Incomplete',
            },
        }
        const badge = statusMap[status] || statusMap.ACTIVE
        return (
            <span
                className={`px-3 py-1 ${badge.bg} ${badge.text} rounded-full text-sm font-medium`}
            >
                {badge.label}
            </span>
        )
    }

    const getInvoiceStatusBadge = (status: string) => {
        const statusMap: Record<
            string,
            { bg: string; text: string; label: string }
        > = {
            paid: {
                bg: 'bg-green-100',
                text: 'text-green-700',
                label: 'Paid',
            },
            open: {
                bg: 'bg-blue-100',
                text: 'text-blue-700',
                label: 'Open',
            },
            draft: {
                bg: 'bg-gray-100',
                text: 'text-gray-700',
                label: 'Draft',
            },
            uncollectible: {
                bg: 'bg-red-100',
                text: 'text-red-700',
                label: 'Failed',
            },
            void: {
                bg: 'bg-gray-100',
                text: 'text-gray-700',
                label: 'Void',
            },
        }
        const badge = statusMap[status] || statusMap.draft
        return (
            <span
                className={`px-2 py-1 ${badge.bg} ${badge.text} rounded-full text-xs font-medium`}
            >
                {badge.label}
            </span>
        )
    }

    const renderContent = () => {
        switch (activeTab) {
            case 'Account':
                return (
                    <>
                        <div className="border-black-6 mb-4 flex w-full flex-col overflow-hidden rounded-2xl border">
                            <FormField
                                className="bg-white"
                                label="Username"
                                value={profile?.username || ''}
                                readOnly
                            />
                            <FormField
                                className="bg-white"
                                label="Full name"
                                value={profile?.name || ''}
                                readOnly
                            />
                            <FormField
                                className="bg-white"
                                label="Bio"
                                value={profile?.bio || ''}
                                readOnly
                            />
                            <FormField
                                className="bg-white"
                                label="Links"
                                value={profile?.website || ''}
                                readOnly
                            />
                        </div>

                        <FormField
                            label="Password"
                            value="************"
                            readOnly
                            rightElement={
                                <button
                                    className="flex items-center gap-1 text-black"
                                    onClick={() => {
                                        onClose()
                                        navigate({
                                            to: ROUTES.CHANGE_PASSWORD,
                                        })
                                    }}
                                >
                                    Change
                                    <Pencil className="h-4 w-4" />
                                </button>
                            }
                            className="rounded-xl border bg-white"
                        />
                    </>
                )
            case 'Currency':
                return (
                    <div className="border-black-6 rounded-2xl border bg-white p-4">
                        <p className="mb-4 text-sm text-gray-600">
                            Select your preferred currency for displaying prices
                        </p>
                        <div className="flex flex-col gap-2">
                            {CURRENCIES.map((curr) => (
                                <button
                                    key={curr.code}
                                    onClick={() =>
                                        handleCurrencyChange(curr.code)
                                    }
                                    className={`flex items-center justify-between rounded-lg border px-4 py-3 transition ${(preferredCurrency ||
                                        profile?.preferredCurrency) ===
                                        curr.code
                                        ? 'border-black bg-gray-100'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <span className="flex items-center gap-3">
                                        {curr.code === 'ORIGINAL' ? (
                                            <span className="font-medium">
                                                Original Currency
                                            </span>
                                        ) : (
                                            <>
                                                <span className="text-lg">
                                                    {curr.symbol}
                                                </span>
                                                <span className="font-medium">
                                                    {curr.code}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                    {(preferredCurrency ||
                                        profile?.preferredCurrency) ===
                                        curr.code && (
                                            <span className="text-green-600">
                                                ✓
                                            </span>
                                        )}
                                </button>
                            ))}
                        </div>
                    </div>
                )
            case 'Archive':
                return (
                    <ArchivePage
                        rootClassNames="border-black-6 lg:h-[30rem] !w-full rounded-2xl border !px-4"
                        asChildren
                    />
                )
            case 'Feedback':
                return (
                    <FeedbackPage
                        asChildren
                        rootClassNames="border-black-6 overflow-hidden rounded-2xl border bg-white !max-h-[30rem] !p-4"
                    />
                )
            case 'Contact & Support':
                return (
                    <div className="flex flex-col gap-6">
                        {/* Pending Deletion Banner */}
                        {profile?.deletionRequest &&
                            profile.deletionRequest.status === 'PENDING' &&
                            user?.uid && (
                                <PendingDeletionBanner
                                    deletionRequest={profile.deletionRequest}
                                    userId={user.uid}
                                />
                            )}

                        <div className="border-black-6 rounded-2xl border bg-white p-6">
                            <div className="mb-6">
                                <h3 className="mb-4 text-lg font-semibold">
                                    Legal
                                </h3>
                                <div className="flex flex-col gap-2">
                                    <a
                                        href="/terms-and-conditions"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between rounded-lg p-3 transition hover:bg-gray-50"
                                    >
                                        <span>Terms and Conditions</span>
                                        <ExternalLink className="h-4 w-4 text-gray-400" />
                                    </a>
                                    <a
                                        href="/privacy-policy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between rounded-lg p-3 transition hover:bg-gray-50"
                                    >
                                        <span>Privacy Policy</span>
                                        <ExternalLink className="h-4 w-4 text-gray-400" />
                                    </a>
                                    <a
                                        href="/terms-of-use"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between rounded-lg p-3 transition hover:bg-gray-50"
                                    >
                                        <span>Terms of Use</span>
                                        <ExternalLink className="h-4 w-4 text-gray-400" />
                                    </a>
                                </div>
                            </div>

                            <div>
                                <h3 className="mb-2 text-lg font-semibold">
                                    Get in Touch
                                </h3>
                                <p className="mb-4 text-sm text-gray-600">
                                    Need help? Our support team is here to
                                    assist you.
                                </p>
                                <div className="flex items-center gap-2 text-sm">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                    <a
                                        href="mailto:support@wtf.style"
                                        className="text-blue-600 hover:underline"
                                    >
                                        support@wtf.style
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Delete Account Section */}
                        <div className="border-black-6 rounded-2xl border bg-white p-6">
                            <h3 className="mb-2 text-lg font-semibold text-red-500">
                                Danger Zone
                            </h3>
                            <p className="mb-4 text-sm text-gray-600">
                                Once you delete your account, there is no going
                                back. Please be certain.
                            </p>
                            <button
                                onClick={() => setShowDeleteAccountModal(true)}
                                className="w-full rounded-xl border border-red-500 px-4 py-3 text-red-500 transition hover:bg-red-50"
                            >
                                Delete Account
                            </button>
                        </div>
                    </div>
                )
            case 'Save Button Colour':
                return (
                    <div className="flex flex-col gap-6">
                        {/* Header and Save Button */}
                        <div className="border-black-6 rounded-2xl border bg-white p-6">
                            <div className="mb-6 text-center">
                                <h3 className="mb-2 text-lg font-semibold">
                                    Choose your save button colour
                                </h3>
                                <p className="text-sm text-gray-600">
                                    We couldn't decide so we're making you
                                </p>
                            </div>

                            {/* Preview Save Button */}
                            <div className="mb-6 flex justify-center">
                                <SaveButton
                                    isSaved={false}
                                    size="lg"
                                    onClick={() => { }}
                                    className="pointer-events-none"
                                />
                            </div>

                            {/* Color Grid */}
                            <div className="flex flex-col gap-3">
                                {[0, 1, 2].map((row) => (
                                    <div
                                        key={row}
                                        className="flex justify-center gap-3"
                                    >
                                        {SAVE_BUTTON_COLORS.slice(
                                            row * 3,
                                            row * 3 + 3
                                        ).map((colorOption) => (
                                            <button
                                                key={colorOption.hex}
                                                onClick={() =>
                                                    setSaveButtonColor(
                                                        colorOption.hex
                                                    )
                                                }
                                                className={`relative flex items-center justify-center rounded-xl px-3 py-3 text-sm font-normal whitespace-nowrap text-white transition hover:scale-105 ${saveButtonColor ===
                                                    colorOption.hex
                                                    ? 'ring-2 ring-black ring-offset-2'
                                                    : ''
                                                    }`}
                                                style={{
                                                    backgroundColor:
                                                        colorOption.hex,
                                                }}
                                                aria-label={`Select ${colorOption.name} color`}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            case 'Blocked Users':
                return (
                    <div className="border-black-6 rounded-2xl border bg-white p-6">
                        <h3 className="mb-4 text-lg font-semibold">Blocked Users</h3>
                        {isLoadingBlockedUsers ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-gray-500">Loading...</div>
                            </div>
                        ) : blockedUsers.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="mb-2 text-base font-medium text-gray-900">
                                    No blocked users
                                </div>
                                <p className="text-sm text-gray-500">
                                    Users you block will appear here. You can unblock them at any time.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {blockedUsers.map((blockedUser) => (
                                    <div
                                        key={blockedUser.id}
                                        className="flex items-center justify-between rounded-xl border border-gray-100 p-3"
                                    >
                                        <Link
                                            to="/profiles/$id"
                                            params={{ id: blockedUser.id }}
                                            className="flex items-center gap-3"
                                            onClick={onClose}
                                        >
                                            {blockedUser.avatarUrl ? (
                                                <img
                                                    src={blockedUser.avatarUrl}
                                                    alt={blockedUser.username}
                                                    className="h-10 w-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-500">
                                                    {blockedUser.username?.charAt(0)?.toUpperCase() ?? '?'}
                                                </div>
                                            )}
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    @{blockedUser.username}
                                                </div>
                                                {blockedUser.name && (
                                                    <div className="text-xs text-gray-500">
                                                        {blockedUser.name}
                                                    </div>
                                                )}
                                            </div>
                                        </Link>
                                        <button
                                            onClick={() => handleUnblock(
                                                blockedUser.id,
                                                blockedUser.username
                                            )}
                                            disabled={isUnblocking}
                                            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm transition hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Unblock
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )
            case 'Subscription':
                if (isLoadingSubscription) {
                    return (
                        <div className="flex items-center justify-center py-20">
                            <div className="text-gray-600">
                                Loading subscription...
                            </div>
                        </div>
                    )
                }

                const percentage = platformSubscription
                    ? (platformSubscription.tryOnsUsed /
                        platformSubscription.tryOnsLimit) *
                    100
                    : 0

                const currentPlan = platformSubscription?.plan || 'FREE'
                const canUpgrade = currentPlan !== 'MAX'
                const canDowngrade = currentPlan !== 'FREE'

                return (
                    <div className="flex flex-col gap-6">
                        {/* Current Plan Card */}
                        <div className="border-black-6 rounded-2xl border bg-white p-6">
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-lg font-semibold">
                                    Current Plan
                                </h3>
                                {platformSubscription &&
                                    getStatusBadge(platformSubscription.status)}
                            </div>
                            <p className="mb-4 text-2xl font-bold">
                                {platformSubscription?.plan || 'FREE'} Plan
                            </p>

                            {/* Upgrade Button Only */}
                            {canUpgrade && (
                                <button
                                    onClick={handleUpgrade}
                                    className="w-full rounded-xl bg-black px-4 py-2 text-white transition hover:bg-gray-800"
                                >
                                    Upgrade Plan
                                </button>
                            )}
                        </div>

                        {/* Pending Downgrade Warning */}
                        {platformSubscription?.scheduledPlan && (
                            <div className="rounded-2xl border-2 border-yellow-200 bg-yellow-50 p-6">
                                <div className="flex items-start gap-3">
                                    <svg
                                        className="mt-0.5 h-6 w-6 flex-shrink-0 text-yellow-600"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <div className="flex-1">
                                        <h4 className="mb-1 text-sm font-semibold text-yellow-800">
                                            Subscription Change Scheduled
                                        </h4>
                                        <p className="mb-3 text-sm text-yellow-800">
                                            Your subscription will be downgraded
                                            to{' '}
                                            <span className="font-medium">
                                                {
                                                    platformSubscription.scheduledPlan
                                                }
                                            </span>{' '}
                                            on{' '}
                                            <span className="font-medium">
                                                {formatDate(
                                                    platformSubscription.currentPeriodEnd
                                                )}
                                            </span>
                                            . You'll keep your current plan
                                            benefits until then.
                                        </p>
                                        <button
                                            onClick={handleReactivate}
                                            className="rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-yellow-700"
                                        >
                                            Keep Current Subscription
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Try-On Credits */}
                        <div className="border-black-6 rounded-2xl border bg-white p-6">
                            <h3 className="mb-2 text-lg font-semibold">
                                Try-On Credits
                            </h3>
                            <p className="mb-1 text-3xl font-bold">
                                {platformSubscription?.tryOnsRemaining || 0}
                            </p>
                            <p className="mb-4 text-sm text-gray-600">
                                credits remaining out of{' '}
                                {platformSubscription?.tryOnsLimit || 0} monthly
                                credits
                            </p>
                            {/* Progress bar */}
                            <div className="h-2 w-full rounded-full bg-gray-200">
                                <div
                                    className="h-2 rounded-full bg-blue-500 transition-all"
                                    style={{
                                        width: `${Math.min(percentage, 100)}%`,
                                    }}
                                />
                            </div>
                        </div>

                        {/* Billing Information */}
                        {platformSubscription && currentPlan !== 'FREE' && (
                            <div className="border-black-6 rounded-2xl border bg-white p-6">
                                <h3 className="mb-4 text-lg font-semibold">
                                    Billing Information
                                </h3>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">
                                        {platformSubscription.scheduledPlan
                                            ? 'Downgrade effective date'
                                            : 'Next billing date'}
                                    </span>
                                    <span className="font-medium">
                                        {formatDate(
                                            platformSubscription.currentPeriodEnd
                                        )}
                                    </span>
                                </div>
                                {platformSubscription.scheduledPlan ? (
                                    <>
                                        <div className="mt-3 flex items-center justify-between">
                                            <span className="text-gray-600">
                                                Current amount
                                            </span>
                                            <span className="font-medium">
                                                {pricing
                                                    ? `${pricing[currentPlan.toLowerCase() as 'pro' | 'max'].display}/month`
                                                    : 'Loading...'}
                                            </span>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between">
                                            <span className="text-gray-600">
                                                After downgrade
                                            </span>
                                            <span className="font-medium text-yellow-700">
                                                {platformSubscription.scheduledPlan ===
                                                    'FREE'
                                                    ? 'Free'
                                                    : pricing
                                                        ? `${pricing[platformSubscription.scheduledPlan.toLowerCase() as 'pro' | 'max'].display}/month`
                                                        : 'Loading...'}
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="mt-3 flex items-center justify-between">
                                        <span className="text-gray-600">
                                            Amount
                                        </span>
                                        <span className="font-medium">
                                            {pricing
                                                ? `${pricing[currentPlan.toLowerCase() as 'pro' | 'max'].display}/month`
                                                : 'Loading...'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Billing History */}
                        {platformSubscription && currentPlan !== 'FREE' && (
                            <div className="border-black-6 rounded-2xl border bg-white p-6">
                                <h3 className="mb-4 text-lg font-semibold">
                                    Billing History
                                </h3>
                                {isLoadingBillingHistory ? (
                                    <div className="py-8 text-center text-gray-600">
                                        Loading invoices...
                                    </div>
                                ) : billingHistory?.invoices &&
                                    billingHistory.invoices.length > 0 ? (
                                    <div className="space-y-2">
                                        {billingHistory.invoices
                                            .slice(0, 6)
                                            .map((invoice) => (
                                                <div
                                                    key={invoice.id}
                                                    className="border-black-6 flex items-center justify-between rounded-lg border p-3"
                                                >
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium">
                                                                {invoice.number ||
                                                                    'Invoice'}
                                                            </span>
                                                            {getInvoiceStatusBadge(
                                                                invoice.status
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-gray-600">
                                                            {formatDate(
                                                                invoice.date
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-sm font-semibold">
                                                            {invoice.currency}{' '}
                                                            {invoice.amount.toFixed(
                                                                2
                                                            )}
                                                        </span>
                                                        {invoice.hostedInvoiceUrl && (
                                                            <a
                                                                href={
                                                                    invoice.hostedInvoiceUrl
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="border-black-6 rounded-lg border px-3 py-1 text-xs transition hover:bg-gray-50"
                                                            >
                                                                View
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                ) : (
                                    <div className="rounded-lg bg-gray-50 py-8 text-center">
                                        <p className="text-gray-600">
                                            No invoices yet
                                        </p>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Your billing history will appear
                                            here
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Downgrade and Cancel Actions */}
                        {currentPlan !== 'FREE' && (
                            <div className="flex flex-col gap-2">
                                {canDowngrade && (
                                    <button
                                        onClick={handleDowngrade}
                                        className="border-black-6 w-full rounded-xl border px-4 py-2 transition hover:bg-gray-50"
                                    >
                                        Downgrade Plan
                                    </button>
                                )}
                                <button
                                    onClick={handleCancel}
                                    className="w-full rounded-xl border border-red-500 px-4 py-2 text-red-500 transition hover:bg-red-50"
                                >
                                    Cancel Subscription
                                </button>
                            </div>
                        )}
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="flex max-h-[90vh] w-full rounded-2xl text-black"
        >
            {/* Sidebar */}
            <aside className="flex w-72 flex-col rounded-l-2xl bg-white p-6">
                {/* Avatar */}
                {/* Profile Picture */}
                <div className="relative mb-8 flex flex-col items-center gap-4">
                    <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
                        {profile?.avatarUrl ? (
                            <img
                                src={profile.avatarUrl}
                                alt="Profile"
                                className="h-full w-full rounded-full object-cover"
                                loading="eager"
                            />
                        ) : (
                            <User className="text-black" size={48} />
                        )}
                        <button className="absolute right-0 bottom-0 z-[9999] flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-gray-600 p-1 text-xs text-white hover:bg-gray-400 hover:text-gray-100">
                            <Pencil size={10} />
                        </button>
                    </div>
                </div>

                {/* Menu */}
                <nav className="flex flex-col gap-2 text-sm">
                    {menuItems.map((item) => (
                        <button
                            key={item}
                            onClick={() => setActiveTab(item)}
                            className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-left transition ${activeTab === item
                                ? 'bg-gray-100 font-medium'
                                : 'hover:bg-gray-100'
                                }`}
                        >
                            {item}
                            {item === 'Account' && (
                                <span className="ml-2 h-2 w-2 rounded-full bg-green-500"></span>
                            )}
                        </button>
                    ))}
                </nav>

                {/* Footer */}
                <div className="mt-auto flex flex-col gap-2 pt-6">
                    {/* <button className="flex cursor-pointer items-center justify-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm hover:bg-gray-300">
                        <Slack size={14} className="text-gray-700" />
                        Join us on Slack
                    </button> */}
                    <a
                        href="https://chromewebstore.google.com/detail/what-the-f-are-you-wearin/pkmphplmpbcebmmddkckfckhejgcgjbi"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                            posthog?.capture(
                                AnalyticsEvents.EXTENSION_DOWNLOAD_CLICKED
                            )
                        }}
                        className="flex cursor-pointer items-center justify-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm hover:bg-gray-300"
                    >
                        <Download size={14} className="text-gray-700" />
                        Get the extension
                    </a>
                </div>
            </aside>

            {/* Content */}
            <section className="flex w-2xl flex-col overflow-hidden rounded-2xl bg-gray-100 p-8">
                {/* Header */}
                <div className="mb-8 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">{activeTab}</h2>
                    <button
                        onClick={onClose}
                        className="rounded-full p-1 hover:bg-gray-200"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Dynamic Tab Content with consistent width */}
                <div className="h-[30rem] w-full overflow-y-scroll rounded-2xl">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="w-full"
                        >
                            {renderContent()}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>

            {/* Coming Soon Modal */}
            <Modal
                open={isComingSoonModalOpen}
                onOpenChange={setIsComingSoonModalOpen}
                size="sm"
            >
                <ModalHeader
                    title="Coming Soon"
                    onClose={() => setIsComingSoonModalOpen(false)}
                />
                <ModalContent>
                    <p className="text-center text-gray-600">
                        The browser extension is currently under development and
                        will be available soon! 👀
                    </p>
                </ModalContent>
            </Modal>

            {/* Cancel Subscription Bottom Sheet */}
            {showCancelModal && (
                <Suspense fallback={<div>Loading...</div>}>
                    <CancelSubscriptionBottomSheet
                        open={showCancelModal}
                        onClose={() => setShowCancelModal(false)}
                    />
                </Suspense>
            )}

            {/* Downgrade Subscription Bottom Sheet */}
            {showDowngradeModal && (
                <Suspense fallback={<div>Loading...</div>}>
                    <DowngradeSubscriptionBottomSheet
                        open={showDowngradeModal}
                        onClose={() => setShowDowngradeModal(false)}
                        currentPlan={platformSubscription?.plan || 'FREE'}
                        targetPlan={targetDowngradePlan}
                    />
                </Suspense>
            )}

            {/* Delete Account Bottom Sheet */}
            {showDeleteAccountModal && (
                <Suspense fallback={<div>Loading...</div>}>
                    <DeleteAccountBottomSheet
                        open={showDeleteAccountModal}
                        onClose={() => setShowDeleteAccountModal(false)}
                    />
                </Suspense>
            )}
        </motion.div>
    )
}

export default AccountPopup
